import { PlusCircleIcon } from '@heroicons/react/16/solid'
import { useEffect, useState, useContext } from 'react'
import { AuthContext } from '../AuthContext'
import { format, addDays, subDays, isToday } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { UserCircleIcon, ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon } from '@heroicons/react/20/solid'
import LoadingComponent from './loading'

export default function Visitors() {
    const user = useContext(AuthContext)
    const [visitors, setVisitors] = useState([])
    const [people, setPeople] = useState([])
    const [showForm, setShowForm] = useState(false)
    const [selectedStaffIds, setSelectedStaffIds] = useState([])
    const [selectedStaffNames, setSelectedStaffNames] = useState([])
    const [loading, setLoading] = useState(false)
    const [form, setForm] = useState({
        Date: '',
        Time: '',
        Reason: '',
        Company: '',
        Name: '',
    })
    // Track which date to show visitors for
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return today
    })

    const navigate = useNavigate()

    // Helper to convert LONGBLOB (arraybuffer or base64) to data URL
    const getProfilePicUrl = (profilePic, mimeType = 'image/jpeg') => {
        if (!profilePic) return null
        if (typeof profilePic === 'string' && profilePic.startsWith('data:image')) {
            return profilePic
        }
        if (typeof profilePic === 'string') {
            return `data:${mimeType};base64,${profilePic}`
        }
        if (profilePic instanceof ArrayBuffer || ArrayBuffer.isView(profilePic)) {
            let binary = ''
            const bytes = new Uint8Array(profilePic)
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i])
            }
            const base64 = window.btoa(binary)
            return `data:${mimeType};base64,${base64}`
        }
        return null
    }

    // Fetch visitors for selectedDate
    const fetchVisitors = async (dateToFetch = selectedDate) => {
        try {
            const res = await fetch('https://api.insightos.co.uk/api/getvisitors', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            })
            if (res.ok) {
                const data = await res.json()
                // Only show visitors for selectedDate based on VisitDate (UTC ISO string)
                const year = dateToFetch.getFullYear()
                const month = dateToFetch.getMonth()
                const day = dateToFetch.getDate()

                const filteredVisitors = await Promise.all(
                    data.data
                        .filter(visitor => {
                            if (!visitor.VisitDate) return false
                            const visit = new Date(visitor.VisitDate)
                            return (
                                visit.getFullYear() === year &&
                                visit.getMonth() === month &&
                                visit.getDate() === day
                            )
                        })
                        .map(async visitor => {
                            if (visitor.Arrived && visitor.ProfilePic && typeof visitor.ProfilePic === 'object' && visitor.ProfilePic.data) {
                                const uint8 = new Uint8Array(visitor.ProfilePic.data)
                                let mimeType = 'image/jpeg';
                                if (uint8.length > 4) {
                                    if (uint8[0] === 255 && uint8[1] === 216) mimeType = 'image/jpeg';
                                    else if (uint8[0] === 137 && uint8[1] === 80 && uint8[2] === 78 && uint8[3] === 71) mimeType = 'image/png';
                                }
                                let binary = ''
                                for (let i = 0; i < uint8.length; i++) {
                                    binary += String.fromCharCode(uint8[i])
                                }
                                const base64 = window.btoa(binary)
                                visitor.ProfilePic = `data:${mimeType};base64,${base64}`
                            }
                            return visitor
                        })
                )

                setVisitors(filteredVisitors)
            }
        } catch (err) {
            console.error('Error fetching visitors:', err)
        }
    }

    useEffect(() => {
        // Fetch staff for staff select
        const fetchStaff = async () => {
            try {
                const res = await fetch('https://api.insightos.co.uk/api/staff', {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                })
                if (res.ok) {
                    const data = await res.json()
                    setPeople(data.data)
                }
            } catch (err) {
                console.error('Error fetching staff:', err)
            }
        }
        fetchStaff()
    }, [showForm])

    useEffect(() => {
        fetchVisitors(selectedDate)
        const interval = setInterval(() => {
            fetchVisitors(selectedDate)
        }, 2 * 60 * 1000)
        return () => clearInterval(interval)
        // eslint-disable-next-line
    }, [selectedDate])

    const handleFormChange = (e) => {
        const { name, value, type, selectedOptions } = e.target
        if (name === 'Staff') {
            const ids = Array.from(selectedOptions, (option) => option.value)
            const names = people
                .filter((p) => ids.includes(String(p.id)))
                .map((p) => p.Name)
            setSelectedStaffIds(ids)
            setSelectedStaffNames(names)
        } else {
            setForm({ ...form, [name]: value })
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const payload = {
            ...form,
            StaffIds: selectedStaffIds,
            StaffNames: selectedStaffNames,
        }
        try {
            const res = await fetch('https://api.insightos.co.uk/api/addvisitor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload),
            })
            if (res.ok) {
                setShowForm(false)
                setForm({
                    Name: '',
                    Date: '',
                    Time: '',
                    Reason: '',
                    Company: '',
                })
                setSelectedStaffIds([])
                setSelectedStaffNames([])
                // Refresh visitors for the selected date
                fetchVisitors(selectedDate)
            } else {
                alert('Failed to create visitor')
            }
        } catch (err) {
            console.error('Error creating visitor:', err)
        }
    }

    const handlePassGeneration = async (visitorId) => {
        setLoading(true)
        const response = await fetch("https://api.insightos.co.uk/api/generatepass", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ id: visitorId }),
        })
        setLoading(false)

        const data = await response.json()
        if (data.success) {
            alert(data.message || 'Visitor pass sent. Please check your emails.')
            fetchVisitors(selectedDate)
        } else {
            alert(data.message || 'Failed to generate pass')
        }
    }

    // Date navigation handlers
    const goToPreviousDay = () => setSelectedDate(prev => subDays(prev, 1))
    const goToNextDay = () => setSelectedDate(prev => addDays(prev, 1))
    const goToToday = () => setSelectedDate(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return today
    })

    return (
        <>
            {loading && <LoadingComponent />}

            <div className="border-b mb-5 border-gray-200 pb-5 px-3 sm:px-6">
                <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Visitors</h3>
                    
                    {/* Date Navigation - Responsive */}
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={goToPreviousDay}
                                className="inline-flex items-center rounded-md bg-gray-200 px-2 py-2 text-gray-700 hover:bg-gray-300 transition"
                                title="Previous Day"
                            >
                                <ChevronLeftIcon className="h-5 w-5" />
                            </button>
                            <button
                                type="button"
                                onClick={goToToday}
                                className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm transition ${
                                    isToday(selectedDate)
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                title="Today"
                            >
                                <CalendarDaysIcon className="h-5 w-5 mr-1" />
                                <span className="hidden sm:inline">Today</span>
                            </button>
                            <button
                                type="button"
                                onClick={goToNextDay}
                                className="inline-flex items-center rounded-md bg-gray-200 px-2 py-2 text-gray-700 hover:bg-gray-300 transition"
                                title="Next Day"
                            >
                                <ChevronRightIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <span className="text-sm text-gray-700 font-medium">
                            {format(selectedDate, 'EEE, dd MMM yyyy')}
                        </span>
                    </div>
                    
                    {/* Action Buttons - Responsive */}
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => setShowForm(true)}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 transition"
                        >
                            <PlusCircleIcon className="h-5 w-5 mr-2 text-white" aria-hidden="true" />
                            <span>Create Visitor</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => window.open(window.location.origin + '/visitorlogin', '_blank')}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition"
                        >
                            <UserCircleIcon className="h-5 w-5 mr-2 text-white" aria-hidden="true" />
                            <span>Visitor Login</span>
                        </button>
                    </div>
                </div>
            </div>
            <ul className="divide-y divide-gray-100 px-3 sm:px-6 mt-2">
                {visitors.length === 0 && (
                    <li className="text-center text-gray-500 py-8 bg-white rounded-lg shadow">No visitors for this day.</li>
                )}
                {visitors.map((visitor) => {
                    let bgColor = "bg-sky-300 border-sky-400"
                    if (visitor.Arrived && !visitor.Left) {
                        bgColor = "bg-emerald-300 border-emerald-700"
                    } else if (visitor.Arrived && visitor.Left) {
                        bgColor = "bg-red-500 border-red-700"
                    }

                    const formatTime = (timestamp) => {
                        if (!timestamp) return ""
                        const date = new Date(timestamp)
                        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
                    }

                    let profilePicUrl = null
                    if (visitor.Arrived && visitor.ProfilePic) {
                        if (typeof visitor.ProfilePic === 'string' && visitor.ProfilePic.startsWith('data:image')) {
                            profilePicUrl = visitor.ProfilePic
                        } else if (typeof visitor.ProfilePic === 'object' && visitor.ProfilePic.data) {
                            const uint8 = new Uint8Array(visitor.ProfilePic.data)
                            let binary = ''
                            for (let i = 0; i < uint8.length; i++) {
                                binary += String.fromCharCode(uint8[i])
                            }
                            const base64 = window.btoa(binary)
                            profilePicUrl = `data:image/jpeg;base64,${base64}`
                        } else if (typeof visitor.ProfilePic === 'string') {
                            profilePicUrl = `data:image/jpeg;base64,${visitor.ProfilePic}`
                        }
                    }

                    return (
                        <li
                            key={visitor.id}
                            className={`flex flex-col sm:flex-row sm:items-center rounded-xl border mt-2 shadow-md p-4 gap-4 ${bgColor} transition`}
                        >
                            {/* Visitor Info - Responsive */}
                            <div className="min-w-0 flex-auto flex items-start gap-3">
                                {visitor.Arrived && profilePicUrl && (
                                    <img
                                        src={profilePicUrl}
                                        alt="Profile"
                                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-white shadow shrink-0"
                                    />
                                )}
                                <div className="w-full">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className="text-base sm:text-lg font-bold text-gray-900">{visitor.Name}</span>
                                        {visitor.Company && (
                                            <span className="px-2 py-0.5 rounded bg-white/30 text-xs font-medium text-gray-800">
                                                {visitor.Company}
                                            </span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-900">
                                        <span>
                                            <span className="font-medium">Visit Date:</span>{" "}
                                            {visitor.VisitDate ? format(new Date(visitor.VisitDate), 'dd/MM/yyyy') : ''}
                                        </span>
                                        <span>
                                            <span className="font-medium">Visit Time:</span>{" "}
                                            {visitor.VisitTime}
                                        </span>
                                        <span>
                                            <span className="font-medium">Staff:</span>{" "}
                                            {visitor.StaffName}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-xs text-gray-800">
                                        <span>
                                            <span className="font-semibold">Arrived:</span>{" "}
                                            {visitor.Arrived ? formatTime(visitor.Arrived) : <span className="italic text-gray-400">Not arrived</span>}
                                        </span>
                                        <span>
                                            <span className="font-semibold ">Left:</span>{" "}
                                            {visitor.Left ? formatTime(visitor.Left) : <span className="italic text-gray-400">Not left</span>}
                                        </span>
                                        <span>
                                            <span className="font-semibold">Reason:</span>{" "}
                                            {visitor.Reason}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Actions - Responsive */}
                            <div className="flex flex-wrap sm:flex-col gap-2 items-center sm:items-end justify-end mt-3 sm:mt-0 sm:ml-auto">
                                {visitor.Arrived && !visitor.Left && (
                                    <>
                                    {visitor.Printed === null && (
                                        <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-red-300 text-gray-700 border border-gray-600 font-semibold text-xs shadow">
                                            Pass not printed
                                        </span>
                                    )}
                                    {visitor.Printed && (
                                        <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-white text-green-700 border border-green-600 font-semibold text-xs shadow">
                                            Pass printed by {visitor.Printed}
                                        </span>
                                    )}
                                        <button
                                            className="inline-flex items-center px-3 py-1.5 rounded-lg bg-white text-emerald-700 border border-emerald-600 font-semibold text-xs shadow hover:bg-emerald-50 transition"
                                            onClick={() => handlePassGeneration(visitor.id)}
                                            type="button"
                                        >
                                            Generate Pass
                                        </button>

                                        <button
                                            className="inline-flex items-center px-3 py-1.5 rounded-lg bg-red-700 text-white font-semibold text-xs shadow hover:bg-emerald-800 transition"
                                            onClick={async () => {
                                                try {
                                                    const res = await fetch(`https://api.insightos.co.uk/api/visitorleft/${visitor.id}`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        credentials: 'include',
                                                    });
                                                    if (res.ok) {
                                                        fetchVisitors(selectedDate)
                                                    } else {
                                                        alert('Failed to mark as left')
                                                    }
                                                } catch (err) {
                                                    alert('Error marking as left')
                                                }
                                            }}
                                            type="button"
                                        >
                                            Left
                                        </button>
                                    </>
                                )}
                                {visitor.Arrived && visitor.Left && (
                                    <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-white text-red-700 border border-red-600 font-semibold text-xs shadow">
                                        Left at {formatTime(visitor.Left)}
                                    </span>
                                )}
                                {!visitor.Arrived && !visitor.Left && (
                                    <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-white text-sky-700 border border-sky-400 font-semibold text-xs shadow">
                                        Awaiting Arrival
                                    </span>
                                )}
                            </div>
                        </li>
                    )
                })}
            </ul>
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
                    <form
                        onSubmit={handleSubmit}
                        className="bg-white rounded-lg p-4 sm:p-6 flex flex-col gap-4 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl"
                    >
                        <div className="sticky top-0 bg-white z-10 pb-2 border-b border-gray-200">
                            <h2 className="text-xl font-semibold">Create Visitor</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Name</label>
                                <input
                                    type="text"
                                    name="Name"
                                    value={form.Name}
                                    onChange={handleFormChange}
                                    required
                                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Date</label>
                                <input
                                    type="date"
                                    name="Date"
                                    value={form.Date}
                                    onChange={handleFormChange}
                                    required
                                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Time</label>
                                <input
                                    type="time"
                                    name="Time"
                                    value={form.Time}
                                    onChange={handleFormChange}
                                    required
                                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                />
                            </div>
                            
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Reason</label>
                                <input
                                    type="text"
                                    name="Reason"
                                    value={form.Reason}
                                    onChange={handleFormChange}
                                    required
                                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                />
                            </div>
                            
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Company</label>
                                <input
                                    type="text"
                                    name="Company"
                                    value={form.Company}
                                    onChange={handleFormChange}
                                    required
                                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    placeholder="Enter company name"
                                />
                            </div>
                            
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Staff</label>
                                <select
                                    name="Staff"
                                    multiple
                                    value={selectedStaffIds}
                                    onChange={handleFormChange}
                                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    required
                                    size={3}
                                >
                                    {people.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.Name}
                                        </option>
                                    ))}
                                </select>
                                <span className="text-xs text-gray-500">Hold Ctrl (Windows) or Cmd (Mac) to select multiple</span>
                            </div>
                        </div>
                        
                        <div className="flex gap-2 mt-4 pt-3 border-t border-gray-200">
                            <button
                                type="submit"
                                data-time-saved="20"
                                data-component="visitor-form"
                                data-feature="create-visitor"
                                className="flex-1 rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 transition"
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="flex-1 rounded bg-gray-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </>
    )
}
